import { useEffect, useMemo } from "react";

import { useReadRelays } from "../../../hooks/use-client-relays";
import useTimelineLoader from "../../../hooks/use-timeline-loader";
import { eventStore } from "../../../services/event-store";
import pool from "../../../services/pool";
import { GAME_KIND } from "../types";
import { parseGameEvent } from "../nostr-events";
import { computeElo, getEloTier, isRankedGame, STARTING_ELO, type EloTier } from "../elo";

export interface PlayerElo {
  rating: number;
  tier: EloTier;
  tierName: string;
  tierColor: string;
  rankedGames: number;
}

export default function usePlayerElo(pubkey?: string): PlayerElo {
  const relays = useReadRelays();

  const filters = useMemo(() => {
    if (!pubkey) return undefined;
    return [
      { kinds: [GAME_KIND], authors: [pubkey] },
      { kinds: [GAME_KIND], "#p": [pubkey] },
    ];
  }, [pubkey]);

  const { loader, timeline } = useTimelineLoader("player-elo-" + (pubkey ?? "none"), relays, filters);

  useEffect(() => {
    if (!loader) return;
    const sub = loader(-Infinity).subscribe();
    return () => sub.unsubscribe();
  }, [loader]);

  useEffect(() => {
    if (!pubkey || relays.length === 0) return;
    const sub = pool
      .request(relays, [
        { kinds: [GAME_KIND], authors: [pubkey] },
        { kinds: [GAME_KIND], "#p": [pubkey] },
      ])
      .subscribe((event) => eventStore.add(event));
    return () => sub.unsubscribe();
  }, [pubkey, relays]);

  return useMemo(() => {
    const defaultTier = getEloTier(STARTING_ELO);
    if (!pubkey) return { rating: STARTING_ELO, tier: defaultTier, tierName: defaultTier.name, tierColor: defaultTier.color, rankedGames: 0 };

    const rankedCompleted = timeline
      .map(parseGameEvent)
      .filter((g): g is NonNullable<typeof g> => g !== null && g.status === "complete" && isRankedGame(g))
      .sort((a, b) => {
        // Sort by the original event created_at
        const eventA = timeline.find((e) => parseGameEvent(e)?.gameId === a.gameId);
        const eventB = timeline.find((e) => parseGameEvent(e)?.gameId === b.gameId);
        return (eventA?.created_at ?? 0) - (eventB?.created_at ?? 0);
      });

    const games = rankedCompleted.map((g) => ({
      creator: g.creator,
      opponent: g.opponent,
      winner: g.winner,
      createdAt: 0,
    }));

    const rating = computeElo(pubkey, games);
    const tier = getEloTier(rating);

    return {
      rating,
      tier,
      tierName: tier.name,
      tierColor: tier.color,
      rankedGames: rankedCompleted.length,
    };
  }, [pubkey, timeline]);
}
